import { useCallback } from 'react';
import { isDev } from '../../../lib/utils.ts';
import { api } from '../../../utils/api.ts';
import type { EntityType } from '../../rich-text/types/entity-types.ts';
import type { AiFeedbackEventPayload, AiFeedbackType } from '../types/ai-feedback-types';

type SendFeedbackParams<T extends AiFeedbackType = AiFeedbackType> = {
    feedbackType: T;
    sessionId: string;
    entityType: EntityType;
    payload?: AiFeedbackEventPayload<T>;
};

type SendFeedbackFn = <T extends AiFeedbackType>(params: SendFeedbackParams<T>) => Promise<void>;

export function useAiFeedback(): { sendFeedback: SendFeedbackFn } {
    const recordFeedbackMutation = api.platoUser.command.aiFeedback.recordFeedback.useMutation();

    const sendFeedback: SendFeedbackFn = useCallback(
        async <T extends AiFeedbackType>(params: SendFeedbackParams<T>) => {
            const { feedbackType, sessionId, entityType, payload } = params;
            const mutation = recordFeedbackMutation;

            if (isDev()) {
                if (payload) {
                    console.groupCollapsed(
                        `%c🤖 AI Feedback: ${feedbackType}%c`,
                        'color: white; background: #10b981; padding: 4px 8px; border-radius: 4px;',
                        'color: #34d399; font-weight: bold;',
                    );
                    console.log(`Session: ${sessionId}`);
                    console.log(`Entity Type: ${entityType}`);
                    console.log(
                        `%c ${JSON.stringify(payload, null, 2)}`,
                        'color: #6ee7b7; font-weight: bold;',
                    );
                    console.groupEnd();
                } else {
                    console.log(
                        `%c🤖 AI Feedback: ${feedbackType}%c | Session: ${sessionId} | Entity: ${entityType}`,
                        'color: white; background: #10b981; padding: 4px 8px; border-radius: 4px;',
                        'color: #34d399; font-weight: bold;',
                    );
                }
            }

            try {
                await mutation.mutateAsync({
                    sessionId,
                    feedbackType,
                    entityType,
                    entityId: payload?.entityId,
                    metadata: payload?.metadata as Record<string, unknown> | undefined,
                });
            } catch (error) {
                console.error('Failed to record AI feedback:', error);
                throw error;
            }
        },
        [recordFeedbackMutation],
    );

    return { sendFeedback };
}
